/**
 * AudioRecorderService - Handles audio recording with Web Audio API
 * Records in WAV format at 16kHz mono, chunked into 30-second segments
 */

import MicrophoneCheckService, {
  NEAR_ZERO_RMS,
  SILENCE_TIMEOUT_MS
} from './MicrophoneCheckService';

const SAMPLE_RATE = 16000;
const SEGMENT_DURATION_MS = 30000; // 30 seconds
const LEVEL_POLL_MS = 100;
const MUTE_DEBOUNCE_MS = 1000;

export const MicInterruptReason = {
  DISCONNECT: 'DISCONNECT',
  SILENCE: 'SILENCE',
  DEVICE_CHANGE: 'DEVICE_CHANGE',
  BACKGROUND: 'BACKGROUND'
};

export class AudioRecorderService {
  constructor() {
    this.mediaStream = null;
    this.audioContext = null;
    this.scriptProcessor = null;
    this.source = null;
    this.analyser = null;
    this.analyserBuffer = null;

    this.isRecordingActive = false;
    this.isPausedState = false;
    this.startTime = null;
    this.pausedDuration = 0;
    this.pauseStartTime = null;

    this.currentSegmentData = [];
    this.segmentIndex = 0;
    this.segmentTimer = null;
    this.segmentTimerRemainingMs = null;
    this.segmentTimerStartedAt = null;

    this.onSegmentReadyCallback = null;
    this.onMicInterruptedCallback = null;
    this.onLevelCallback = null;

    this.deviceId = null;
    this.currentRms = 0;
    this.silenceStartedAt = null;
    this.silenceFrozen = false;
    this.levelPollTimer = null;
    this.interruptReason = null;
    this.handlingInterrupt = false;
    this.muteDebounceTimer = null;

    this.boundTrackEnded = this.handleTrackEnded.bind(this);
    this.boundTrackMute = this.handleTrackMute.bind(this);
    this.boundTrackUnmute = this.handleTrackUnmute.bind(this);
    this.boundDeviceChange = this.handleDeviceChange.bind(this);
    this.boundContextStateChange = this.handleContextStateChange.bind(this);
  }

  /**
   * @param {object} [options]
   * @param {string|null} [options.deviceId]
   * @param {Function} [options.onMicInterrupted]
   * @param {Function} [options.onLevel]
   * @param {object} [options.restore] - { segmentIndex, elapsedMs, pausedDuration, deviceId }
   */
  configure(options = {}) {
    if (options.deviceId !== undefined) this.deviceId = options.deviceId;
    if (options.onMicInterrupted) this.onMicInterruptedCallback = options.onMicInterrupted;
    if (options.onLevel) this.onLevelCallback = options.onLevel;
    if (options.restore) {
      this.applyRestoreState(options.restore);
    }
  }

  applyRestoreState(restore) {
    if (typeof restore.segmentIndex === 'number') {
      this.segmentIndex = restore.segmentIndex;
    }
    if (typeof restore.pausedDuration === 'number') {
      this.pausedDuration = restore.pausedDuration;
    }
    if (restore.deviceId) {
      this.deviceId = restore.deviceId;
    }
    if (typeof restore.elapsedMs === 'number') {
      // Reconstruct startTime so getElapsedTime() continues from restored elapsed
      this.startTime = Date.now() - restore.elapsedMs - (this.pausedDuration || 0);
    }
  }

  /**
   * Request microphone permission / open stream for optional deviceId
   * @param {string|null} [deviceId]
   * @returns {Promise<boolean>}
   */
  async requestPermission(deviceId = null) {
    try {
      const targetDeviceId = deviceId ?? this.deviceId;
      this.detachTrackListeners();
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.mediaStream = null;
      }

      this.mediaStream = await MicrophoneCheckService.getUserMediaForDevice(targetDeviceId);
      const track = this.mediaStream.getAudioTracks()[0];
      const actualDeviceId = track?.getSettings?.()?.deviceId || targetDeviceId || null;
      if (actualDeviceId) {
        this.deviceId = actualDeviceId;
        MicrophoneCheckService.setLastDeviceId(actualDeviceId);
      }
      this.attachTrackListeners();
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      return false;
    }
  }

  attachTrackListeners() {
    if (!this.mediaStream) return;
    this.mediaStream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', this.boundTrackEnded);
      track.addEventListener('mute', this.boundTrackMute);
      track.addEventListener('unmute', this.boundTrackUnmute);
    });
  }

  detachTrackListeners() {
    this.clearMuteDebounce();
    if (!this.mediaStream) return;
    this.mediaStream.getAudioTracks().forEach((track) => {
      track.removeEventListener('ended', this.boundTrackEnded);
      track.removeEventListener('mute', this.boundTrackMute);
      track.removeEventListener('unmute', this.boundTrackUnmute);
    });
  }

  handleTrackEnded() {
    this.triggerInterrupt(MicInterruptReason.DISCONNECT);
  }

  handleTrackMute() {
    this.clearMuteDebounce();
    this.muteDebounceTimer = setTimeout(() => {
      this.muteDebounceTimer = null;
      if (!this.isRecordingActive || this.isPausedState) return;
      const track = this.mediaStream?.getAudioTracks?.()[0];
      if (!track || !track.muted) return;
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const interrupted = this.audioContext?.state === 'interrupted';
      // Hidden + muted alone can be a tab-switch quirk; pause immediately for
      // lock/interrupt or when the clinician can see the recovery banner.
      if (!hidden || interrupted || track.readyState === 'ended') {
        this.triggerInterrupt(MicInterruptReason.DISCONNECT);
      }
    }, MUTE_DEBOUNCE_MS);
  }

  handleTrackUnmute() {
    this.clearMuteDebounce();
  }

  clearMuteDebounce() {
    if (this.muteDebounceTimer) {
      clearTimeout(this.muteDebounceTimer);
      this.muteDebounceTimer = null;
    }
  }

  handleDeviceChange() {
    // If the active track is gone or muted after a device list change, interrupt.
    const track = this.mediaStream?.getAudioTracks?.()[0];
    if (!track || track.readyState === 'ended') {
      this.triggerInterrupt(MicInterruptReason.DISCONNECT);
    }
  }

  handleContextStateChange() {
    if (!this.isRecordingActive || this.isPausedState) return;
    const state = this.audioContext?.state;
    if (!state || state === 'running' || state === 'closed') return;
    if (state === 'interrupted') {
      this.triggerInterrupt(MicInterruptReason.DISCONNECT);
      return;
    }
    if (state === 'suspended') {
      this.resumeIfSuspended();
    }
  }

  /**
   * Attempt to resume a suspended AudioContext. Pauses only when resume fails
   * while the tab is visible (hidden suspend is normal; visibility return retries).
   * @returns {Promise<boolean>} true when the context is running
   */
  async resumeIfSuspended() {
    if (!this.audioContext || this.audioContext.state !== 'suspended') {
      return this.audioContext?.state === 'running';
    }
    if (!this.isRecordingActive || this.isPausedState) return false;
    try {
      await this.audioContext.resume();
    } catch (error) {
      console.warn('Failed to resume AudioContext:', error);
    }
    if (!this.isRecordingActive || this.isPausedState) {
      return this.audioContext?.state === 'running';
    }
    const state = this.audioContext?.state;
    if (state === 'running') return true;
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (!hidden && (state === 'suspended' || state === 'interrupted')) {
      this.triggerInterrupt(MicInterruptReason.DISCONNECT);
    }
    return false;
  }

  freezeSilenceDetection() {
    this.silenceFrozen = true;
    this.silenceStartedAt = null;
  }

  unfreezeSilenceDetection() {
    this.silenceFrozen = false;
    this.silenceStartedAt = null;
  }

  triggerInterrupt(reason) {
    if (!this.isRecordingActive) return;

    // Already in an interrupt — allow upgrade to DISCONNECT (e.g. lock killed the track)
    if (this.interruptReason && reason === MicInterruptReason.DISCONNECT
        && this.interruptReason !== MicInterruptReason.DISCONNECT) {
      this.interruptReason = reason;
      this.pauseForInterrupt();
      this.onMicInterruptedCallback?.(reason);
      return;
    }

    if (this.handlingInterrupt) return;
    // User-initiated or background pause should not fire silence interrupts
    if (reason === MicInterruptReason.SILENCE && this.isPausedState) return;
    // Do not replace an existing interrupt with BACKGROUND
    if (reason === MicInterruptReason.BACKGROUND && this.interruptReason) return;

    this.handlingInterrupt = true;
    this.interruptReason = reason;
    this.pauseForInterrupt();
    this.onMicInterruptedCallback?.(reason);
    // Allow subsequent interrupts after UI has reacted
    setTimeout(() => {
      this.handlingInterrupt = false;
    }, 500);
  }

  /**
   * True when the media track is live and the audio graph is usable.
   */
  isCaptureHealthy() {
    const track = this.mediaStream?.getAudioTracks?.()[0];
    if (!track || track.readyState === 'ended' || track.muted) return false;
    if (this.audioContext && (this.audioContext.state === 'closed' || this.audioContext.state === 'interrupted')) {
      return false;
    }
    return true;
  }

  /**
   * Start recording
   * @param {Function} onSegmentReady - Callback called with { index, data } every 30s
   * @param {object} [options]
   * @returns {Promise<boolean>}
   */
  async startRecording(onSegmentReady, options = {}) {
    if (this.isRecordingActive) {
      console.warn('Recording already in progress');
      return false;
    }

    if (options.deviceId) this.deviceId = options.deviceId;
    if (options.onMicInterrupted) this.onMicInterruptedCallback = options.onMicInterrupted;
    if (options.onLevel) this.onLevelCallback = options.onLevel;
    if (options.restore) this.applyRestoreState(options.restore);

    if (!this.mediaStream) {
      const hasPermission = await this.requestPermission(this.deviceId);
      if (!hasPermission) {
        return false;
      }
    }

    this.onSegmentReadyCallback = onSegmentReady;
    // Only reset segment index if not restoring
    if (!options.restore || typeof options.restore.segmentIndex !== 'number') {
      this.segmentIndex = 0;
    }
    this.currentSegmentData = [];
    if (!this.startTime) {
      this.startTime = Date.now();
      this.pausedDuration = options.restore?.pausedDuration || 0;
    }
    this.isPausedState = false;
    this.pauseStartTime = null;
    this.interruptReason = null;
    this.silenceStartedAt = null;
    this.silenceFrozen = false;

    try {
      await this.buildAudioGraph();
      this.isRecordingActive = true;
      this.startSegmentTimer(SEGMENT_DURATION_MS);
      this.startLevelPolling();

      if (navigator.mediaDevices?.addEventListener) {
        navigator.mediaDevices.addEventListener('devicechange', this.boundDeviceChange);
      }

      return true;
    } catch (error) {
      console.error('Error starting recording:', error);
      this.cleanup();
      return false;
    }
  }

  async buildAudioGraph() {
    this.teardownAudioGraph(false);

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: SAMPLE_RATE
    });
    this.audioContext.addEventListener('statechange', this.boundContextStateChange);

    this.source = this.audioContext.createMediaStreamSource(this.mediaStream);

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.8;
    this.analyserBuffer = new Uint8Array(this.analyser.fftSize);

    const bufferSize = 4096;
    this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

    this.scriptProcessor.onaudioprocess = (event) => {
      if (!this.isRecordingActive || this.isPausedState) return;

      const inputData = event.inputBuffer.getChannelData(0);
      const pcmData = new Int16Array(inputData.length);
      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]));
        pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      this.currentSegmentData.push(pcmData);
    };

    // source -> analyser (levels) and source -> scriptProcessor (PCM)
    this.source.connect(this.analyser);
    this.source.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioContext.destination);
  }

  teardownAudioGraph(stopStream = false) {
    if (this.scriptProcessor) {
      try { this.scriptProcessor.disconnect(); } catch { /* ignore */ }
      this.scriptProcessor.onaudioprocess = null;
      this.scriptProcessor = null;
    }
    if (this.analyser) {
      try { this.analyser.disconnect(); } catch { /* ignore */ }
      this.analyser = null;
      this.analyserBuffer = null;
    }
    if (this.source) {
      try { this.source.disconnect(); } catch { /* ignore */ }
      this.source = null;
    }
    if (this.audioContext) {
      this.audioContext.removeEventListener('statechange', this.boundContextStateChange);
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    if (stopStream && this.mediaStream) {
      this.detachTrackListeners();
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  startSegmentTimer(durationMs = SEGMENT_DURATION_MS) {
    this.clearSegmentTimer();
    this.segmentTimerRemainingMs = durationMs;
    this.segmentTimerStartedAt = Date.now();
    this.segmentTimer = setTimeout(() => {
      this.flushSegment();
      this.startSegmentTimer(SEGMENT_DURATION_MS);
    }, durationMs);
  }

  clearSegmentTimer() {
    if (this.segmentTimer) {
      clearTimeout(this.segmentTimer);
      this.segmentTimer = null;
    }
    if (this.segmentTimerStartedAt != null && this.segmentTimerRemainingMs != null) {
      const elapsed = Date.now() - this.segmentTimerStartedAt;
      this.segmentTimerRemainingMs = Math.max(0, this.segmentTimerRemainingMs - elapsed);
    }
    this.segmentTimerStartedAt = null;
  }

  pauseSegmentTimer() {
    this.clearSegmentTimer();
  }

  resumeSegmentTimer() {
    const remaining = this.segmentTimerRemainingMs ?? SEGMENT_DURATION_MS;
    this.startSegmentTimer(remaining > 0 ? remaining : SEGMENT_DURATION_MS);
  }

  startLevelPolling() {
    this.stopLevelPolling();
    this.levelPollTimer = setInterval(() => {
      this.pollLevel();
    }, LEVEL_POLL_MS);
  }

  stopLevelPolling() {
    if (this.levelPollTimer) {
      clearInterval(this.levelPollTimer);
      this.levelPollTimer = null;
    }
  }

  pollLevel() {
    if (!this.analyser || !this.isRecordingActive) {
      this.currentRms = 0;
      this.onLevelCallback?.(0);
      return;
    }

    if (this.isPausedState) {
      this.currentRms = 0;
      this.silenceStartedAt = null;
      this.onLevelCallback?.(0);
      return;
    }

    const rms = MicrophoneCheckService.computeRms(this.analyser, this.analyserBuffer);
    this.currentRms = rms;
    this.onLevelCallback?.(rms);

    const tabHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (this.silenceFrozen || tabHidden) {
      this.silenceStartedAt = null;
      return;
    }

    if (rms < NEAR_ZERO_RMS) {
      if (!this.silenceStartedAt) {
        this.silenceStartedAt = Date.now();
      } else if (Date.now() - this.silenceStartedAt >= SILENCE_TIMEOUT_MS) {
        this.silenceStartedAt = null;
        this.triggerInterrupt(MicInterruptReason.SILENCE);
      }
    } else {
      this.silenceStartedAt = null;
    }
  }

  getCurrentRms() {
    return this.currentRms;
  }

  flushSegment() {
    if (this.currentSegmentData.length === 0) return;

    const wavData = this.createWavFromPcm(this.currentSegmentData);
    const base64Data = this.arrayBufferToBase64(wavData);

    const segmentInfo = {
      index: this.segmentIndex,
      data: base64Data
    };

    this.segmentIndex++;
    this.currentSegmentData = [];

    if (this.onSegmentReadyCallback) {
      this.onSegmentReadyCallback(segmentInfo);
    }
  }

  /**
   * User-initiated pause
   */
  pauseRecording() {
    if (!this.isRecordingActive || this.isPausedState) return;

    this.isPausedState = true;
    this.pauseStartTime = Date.now();
    this.pauseSegmentTimer();
    this.clearMuteDebounce();
    this.silenceStartedAt = null;
    this.interruptReason = null;
  }

  /**
   * Pause due to mic interrupt — holds current segment
   * @param {string|null} [reason] - When provided, stores interrupt reason for resume path
   */
  pauseForInterrupt(reason = undefined) {
    if (!this.isRecordingActive) return;
    if (!this.isPausedState) {
      this.isPausedState = true;
      this.pauseStartTime = Date.now();
      this.pauseSegmentTimer();
    }
    if (reason !== undefined) {
      this.interruptReason = reason;
    }
    this.clearMuteDebounce();
    this.silenceStartedAt = null;
    this.currentRms = 0;
    this.onLevelCallback?.(0);
  }

  /**
   * Resume recording. Optionally switch/reacquire device.
   * @param {string|null} [deviceId]
   * @returns {Promise<boolean>}
   */
  async resumeRecording(deviceId = null) {
    if (!this.isRecordingActive || !this.isPausedState) return false;

    const trackDead = !this.mediaStream
      || this.mediaStream.getAudioTracks().length === 0
      || this.mediaStream.getAudioTracks().every((t) => t.readyState === 'ended');
    const switchingDevice = deviceId != null && deviceId !== this.deviceId;
    const needsNewStream = switchingDevice
      || trackDead
      || !this.isCaptureHealthy()
      || this.interruptReason === MicInterruptReason.DISCONNECT
      || this.interruptReason === MicInterruptReason.DEVICE_CHANGE;

    if (needsNewStream) {
      const ok = await this.requestPermission(deviceId ?? this.deviceId);
      if (!ok) return false;
      try {
        await this.buildAudioGraph();
      } catch (error) {
        console.error('Failed to rebuild audio graph on resume:', error);
        return false;
      }
    } else if (this.audioContext?.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.error('Failed to resume AudioContext:', error);
        // Fall back to rebuilding the graph
        const ok = await this.requestPermission(deviceId ?? this.deviceId);
        if (!ok) return false;
        try {
          await this.buildAudioGraph();
        } catch (rebuildError) {
          console.error('Failed to rebuild audio graph after suspended context:', rebuildError);
          return false;
        }
      }
    }

    if (this.pauseStartTime) {
      this.pausedDuration += Date.now() - this.pauseStartTime;
    }
    this.isPausedState = false;
    this.pauseStartTime = null;
    this.interruptReason = null;
    this.silenceStartedAt = null;
    this.silenceFrozen = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    this.resumeSegmentTimer();
    return true;
  }

  /**
   * Snapshot for sessionStorage persistence
   */
  getSessionSnapshot() {
    return {
      segmentIndex: this.segmentIndex,
      elapsedMs: this.getElapsedTime() * 1000,
      pausedDuration: this.pausedDuration
        + (this.isPausedState && this.pauseStartTime ? Date.now() - this.pauseStartTime : 0),
      deviceId: this.deviceId,
      isPaused: this.isPausedState,
      pauseReason: this.interruptReason || null
    };
  }

  getDeviceId() {
    return this.deviceId;
  }

  getInterruptReason() {
    return this.interruptReason;
  }

  /**
   * Stop recording and return final segment
   * @returns {{ totalSegments: number, finalSegment: { index: number, data: string } | null }}
   */
  stopRecording() {
    // Always release media resources, even if already marked inactive
    if (!this.isRecordingActive) {
      this.cleanup();
      return { totalSegments: this.segmentIndex, finalSegment: null };
    }

    this.clearSegmentTimer();
    this.segmentTimerRemainingMs = null;
    this.stopLevelPolling();

    if (navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.boundDeviceChange);
    }

    let finalSegment = null;
    if (this.currentSegmentData.length > 0) {
      const wavData = this.createWavFromPcm(this.currentSegmentData);
      const base64Data = this.arrayBufferToBase64(wavData);
      finalSegment = {
        index: this.segmentIndex,
        data: base64Data
      };
      this.segmentIndex++;
    }

    const totalSegments = this.segmentIndex;

    this.cleanup();

    return { totalSegments, finalSegment };
  }

  cleanup() {
    this.isRecordingActive = false;
    this.isPausedState = false;
    this.stopLevelPolling();
    this.clearSegmentTimer();
    this.segmentTimerRemainingMs = null;

    if (navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.boundDeviceChange);
    }

    this.teardownAudioGraph(true);

    this.currentSegmentData = [];
    this.currentRms = 0;
    this.silenceStartedAt = null;
    this.silenceFrozen = false;
    this.interruptReason = null;
    this.handlingInterrupt = false;
    this.clearMuteDebounce();
  }

  getElapsedTime() {
    if (!this.startTime) return 0;

    let elapsed = Date.now() - this.startTime - this.pausedDuration;

    if (this.isPausedState && this.pauseStartTime) {
      elapsed -= (Date.now() - this.pauseStartTime);
    }

    return Math.floor(elapsed / 1000);
  }

  isRecording() {
    return this.isRecordingActive;
  }

  isPaused() {
    return this.isPausedState;
  }

  getCurrentSegmentIndex() {
    return this.segmentIndex;
  }

  createWavFromPcm(pcmChunks) {
    let totalLength = 0;
    for (const chunk of pcmChunks) {
      totalLength += chunk.length;
    }

    const combined = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of pcmChunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }

    const wavBuffer = new ArrayBuffer(44 + combined.length * 2);
    const view = new DataView(wavBuffer);

    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + combined.length * 2, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, SAMPLE_RATE, true);
    view.setUint32(28, SAMPLE_RATE * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, combined.length * 2, true);

    const dataView = new Int16Array(wavBuffer, 44);
    dataView.set(combined);

    return wavBuffer;
  }

  writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}

export default AudioRecorderService;
